// apps/web/src/App.tsx
import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense } from 'react';
import Layout from './components/Layout';
import LoadingSpinner from './components/LoadingSpinner';
import { LoginPage } from './pages/LoginPage';
import ProfilePage from './pages/ProfilePage';
import DirectoryPage from './pages/DirectoryPage';
import PublicProfilePage from './pages/PublicProfilePage';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminSettings from './pages/admin/AdminSettings';
import NotFoundPage from './pages/NotFoundPage';
import { useAuth } from './hooks/useAuth';

function Lazy({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<LoadingSpinner />}>{children}</Suspense>;
}

/** 需登入 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/** 主管理者或副管理者（審核儀表板） */
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.isAdmin) return <Navigate to="/directory" replace />;
  return <>{children}</>;
}

/** 僅主管理者（會員管理、設定、代編） */
function OwnerRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.isOwner) return <Navigate to="/admin" replace />;
  return <>{children}</>;
}

function PublicLayout() {
  return <Layout />;
}

function ProtectedLayout() {
  return (
    <ProtectedRoute>
      <Layout />
    </ProtectedRoute>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<LoginPage />} />

      <Route element={<PublicLayout />}>
        <Route
          path="/directory"
          element={
            <Lazy>
              <DirectoryPage />
            </Lazy>
          }
        />
        <Route path="/directory/:id" element={<PublicProfilePage />} />
        <Route path="/" element={<Navigate to="/directory" replace />} />
      </Route>

      <Route element={<ProtectedLayout />}>
        <Route
          path="/profile"
          element={
            <Lazy>
              <ProfilePage />
            </Lazy>
          }
        />
        {/* 僅主管理者可代編 */}
        <Route
          path="/profile/edit/:userId"
          element={
            <OwnerRoute>
              <Lazy>
                <ProfilePage />
              </Lazy>
            </OwnerRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <Lazy>
                <AdminDashboard />
              </Lazy>
            </AdminRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <OwnerRoute>
              <Lazy>
                <AdminUsers />
              </Lazy>
            </OwnerRoute>
          }
        />
        <Route
          path="/admin/settings"
          element={
            <OwnerRoute>
              <Lazy>
                <AdminSettings />
              </Lazy>
            </OwnerRoute>
          }
        />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
